import { useState } from 'react';
import { Plus, Trash2, Edit2, Pin } from 'lucide-react';
import type { CreatePrinterNoteInput, UpdatePrinterNoteInput } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { Spinner } from '@/components/Spinner';
import {
  usePrinterNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from '../hooks';

interface PrinterNotesProps {
  printerId: string;
}

export function PrinterNotes({ printerId }: PrinterNotesProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { data: notes, isLoading } = usePrinterNotes(printerId);
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();

  const [formData, setFormData] = useState<Partial<CreatePrinterNoteInput | UpdatePrinterNoteInput>>({
    isPinned: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setFormData({ isPinned: false });
    setErrors({});
    setIsAddingNote(false);
    setEditingNote(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setIsAddingNote(true);
  };

  const handleOpenEdit = (noteId: string) => {
    const note = notes?.find((n) => n.id === noteId);
    if (note) {
      setFormData({
        title: note.title || undefined,
        content: note.content,
        isPinned: note.isPinned,
      });
      setEditingNote(noteId);
    }
  };

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.content?.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (editingNote) {
        const updateData: UpdatePrinterNoteInput = {
          title: formData.title || undefined,
          content: formData.content!,
          isPinned: formData.isPinned || false,
        };
        await updateNote.mutateAsync({
          printerId,
          noteId: editingNote,
          input: updateData,
        });
      } else {
        const createData: CreatePrinterNoteInput = {
          title: formData.title || undefined,
          content: formData.content!,
          isPinned: formData.isPinned || false,
        };
        await createNote.mutateAsync({
          printerId,
          input: createData,
        });
      }
      resetForm();
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteNote.mutateAsync({ printerId, noteId: deleteTarget });
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Notes</h3>
        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={handleOpenAdd}>
          Add Note
        </Button>
      </div>

      {!notes || notes.length === 0 ? (
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-foreground-muted">No notes yet. Add your first note to track important information.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="rounded-lg border border-border bg-background-subtle p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {note.isPinned && (
                      <Pin className="h-4 w-4 text-primary" />
                    )}
                    {note.title && (
                      <h4 className="font-medium text-foreground">{note.title}</h4>
                    )}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground-muted">
                    {note.content}
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-foreground-muted">
                    {note.user && (
                      <span className="font-medium">{note.user.name}</span>
                    )}
                    <span>•</span>
                    <span>{new Date(note.createdAt).toLocaleString()}</span>
                    {note.updatedAt !== note.createdAt && (
                      <span className="italic">(edited)</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenEdit(note.id)}
                    title="Edit note"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteTarget(note.id)}
                    title="Delete note"
                  >
                    <Trash2 className="h-4 w-4 text-error" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      <SlideOver
        open={isAddingNote || !!editingNote}
        onClose={resetForm}
        title={editingNote ? 'Edit Note' : 'Add Note'}
      >
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <div className="flex-1 space-y-6 overflow-y-auto p-4">
            <FormSection title="Note Details" defaultOpen>
              <div className="space-y-4">
                <Input
                  label="Title"
                  placeholder="Optional title"
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value || undefined)}
                />
                <Textarea
                  label="Content"
                  placeholder="Add your note..."
                  value={formData.content || ''}
                  onChange={(e) => handleChange('content', e.target.value)}
                  error={errors.content}
                  rows={8}
                  required
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isPinned || false}
                    onChange={(e) => handleChange('isPinned', e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Pin this note</span>
                </label>
              </div>
            </FormSection>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-border bg-background-subtle px-4 py-3">
            <Button variant="ghost" onClick={resetForm} disabled={createNote.isPending || updateNote.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={createNote.isPending || updateNote.isPending}>
              {editingNote ? 'Save Changes' : 'Add Note'}
            </Button>
          </div>
        </form>
      </SlideOver>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        loading={deleteNote.isPending}
      />
    </div>
  );
}
